<template>
  <div class="alloc-card">
    <p class="alloc-title">Portfolio Allocation</p>
    <p class="alloc-sub">Distribution across investment products</p>

    <div class="alloc-body">
      <!-- Donut -->
      <div class="donut-wrap">
        <svg class="donut" viewBox="0 0 130 130">
          <g transform="rotate(-90 65 65)">
            <!-- Track (empty state) -->
            <circle
              v-if="!hasData"
              cx="65"
              cy="65"
              :r="radius"
              fill="none"
              stroke="#EEF1F6"
              :stroke-width="thickness"
            />
            <!-- Segments -->
            <circle
              v-for="(seg, i) in segments"
              :key="i"
              cx="65"
              cy="65"
              :r="radius"
              fill="none"
              :stroke="seg.color"
              :stroke-width="thickness"
              :stroke-dasharray="seg.dashArray"
              :stroke-dashoffset="seg.dashOffset"
            />
          </g>
        </svg>
        <div class="donut-center">
          <span class="donut-count">{{ productCount }}</span>
          <span class="donut-label">{{ productCount === 1 ? "Product" : "Products" }}</span>
        </div>
      </div>

      <!-- Legend -->
      <div class="legend">
        <template v-if="hasData">
          <div v-for="(row, i) in allocation" :key="i" class="legend-row">
            <span class="legend-dot" :style="{ background: row.color }"></span>
            <span class="legend-name">{{ row.name }}</span>
            <span class="legend-pct">{{ Math.round(row.pct) }}%</span>
          </div>
        </template>
        <template v-else>
          <div v-for="n in 3" :key="n" class="legend-row skeleton">
            <span class="legend-dot ghost"></span>
            <span class="legend-bar"></span>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "InvestAllocationDonut",
  props: {
    // [{ name, value, pct, color }] sorted desc.
    allocation: { type: Array, default: () => [] },
  },
  data() {
    return {
      radius: 42,
      thickness: 22,
      gap: 4, // arc-length gap between segments
    };
  },
  computed: {
    hasData() {
      return this.allocation.length > 0;
    },
    productCount() {
      return this.allocation.length;
    },
    circumference() {
      return 2 * Math.PI * this.radius;
    },
    segments() {
      const C = this.circumference;
      let cursor = 0;
      return this.allocation.map((row) => {
        const segLen = (row.pct / 100) * C;
        const visible = Math.max(segLen - this.gap, 0.5);
        const seg = {
          color: row.color,
          dashArray: `${visible} ${C - visible}`,
          dashOffset: -cursor,
        };
        cursor += segLen;
        return seg;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.alloc-card {
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #e8ecf4;
  border-radius: 8px;
  padding: 16px;

  @media (min-width: 1024px) {
    padding: 20px 22px;
  }
}

.alloc-title {
  margin: 0;
  font-size: 12px;
  font-weight: 500;
  color: #001240;
}

.alloc-sub {
  margin: 2px 0 0;
  font-size: 11px;
  color: #8791a7;
}

.alloc-body {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-top: 16px;

  @media (min-width: 1024px) {
    flex: 1;
    gap: 28px;
    margin-top: 20px;
  }
}

.donut-wrap {
  position: relative;
  flex-shrink: 0;
  width: 130px;
  height: 130px;

  @media (min-width: 1024px) {
    width: 160px;
    height: 160px;
  }
}

.donut {
  width: 100%;
  height: 100%;
}

.donut-center {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.donut-count {
  font-size: 22px;
  font-weight: 500;
  color: #001240;
  line-height: 1;

  @media (min-width: 1024px) {
    font-size: 28px;
  }
}

.donut-label {
  margin-top: 2px;
  font-size: 10px;
  color: #8791a7;
}

.legend {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.legend-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.legend-dot {
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  border-radius: 3px;

  &.ghost {
    background: #e8ecf4;
  }
}

.legend-name {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  color: #4f5877;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.legend-pct {
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 500;
  color: #001240;
}

.legend-bar {
  flex: 1;
  height: 8px;
  border-radius: 4px;
  background: #f0f2f7;
}

.skeleton {
  opacity: 0.9;
}
</style>
